/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { pick, isEqual, flow, cloneDeep, pickBy, find, extend } from 'lodash';

export const STEP_DEFAULTS = {
  title: 'New Step',
  stepTitle: 'New Step',

  actionTrigger: 'Accept',
  sourceType: 'Risk',
  riskThreshold: null,
  triggerType: null,
  groupId: 0,

  expanded: false,
  order: 0,
  active: true,
  startDelta: undefined,
  endDelta: undefined,
  assignees: [],
  reviewers: [],
  verifiers: [],
  invalid: false,
  serverError: false,

  justCreated: false,
  validationErrors: {},
};

export const STEP_CONTENT_PROPS = [
  'assignees',
  'description',
  'endDelta',
  'notifyAssignee',
  'reviewers',
  'startDelta',
  'title',
  'verifiers',
];

export const SAVEABLE_STEP_PROPS = [
  'id',
  'active',
  'order',
  'stepTitle',
  'actionTrigger',
  'riskThreshold',
  ...STEP_CONTENT_PROPS,
];

export function updateModified(itemId, newData, modifiedItems, originalItems) {
  let result = find(modifiedItems, { id: itemId });
  if (result) {
    extend(result, newData);
  } else {
    result = find(originalItems, { id: itemId });
    if (result) {
      newData = extend({}, result, newData);
    }
    result = cloneDeep(newData);
    modifiedItems.push(result);
  }
  return result;
}

export function triggerChanged(step, payloadData) {
  return ['sourceType', 'triggerType', 'actionTrigger', 'riskThreshold'].some(prop => {
    return (
      (step !== undefined && payloadData.hasOwnProperty('actionTrigger')) ||
      (step !== undefined && payloadData.hasOwnProperty('riskThreshold')) ||
      (step !== undefined && payloadData.hasOwnProperty(prop) && step[prop] !== payloadData[prop])
    );
  });
}

export function isStepDirty(step, originalSteps) {
  const removeInternalProps = step => {
    return pick(step, SAVEABLE_STEP_PROPS);
  };
  const isDirty = step => {
    return !isEqual(
      step,
      originalSteps.map(removeInternalProps).find(origStep => origStep.id === step.id)
    );
  };
  return flow(cloneDeep, removeInternalProps, isDirty)(step);
}

export function haveEqualStatusTransition(step, currentStep) {
  return (
    !!step.actionTrigger &&
    !!step.triggerType &&
    currentStep.id !== step.id &&
    currentStep.sourceType === step.sourceType &&
    currentStep.triggerType === step.triggerType &&
    currentStep.actionTrigger === step.actionTrigger
  );
}

export function haveEqualRiskThreshold(step, currentStep) {
  return (
    step.id !== currentStep.id &&
    step.riskThreshold &&
    !!step.riskThreshold.id &&
    currentStep.riskThreshold &&
    !!currentStep.riskThreshold.id &&
    currentStep.riskThreshold.id === step.riskThreshold.id
  );
}

function validateDuplicatedTriggers(step, steps) {
  const duplicatedTriggers =
    steps.filter(currentStep => {
      return haveEqualStatusTransition(step, currentStep) || haveEqualRiskThreshold(step, currentStep);
    }).length >= 1;

  return duplicatedTriggers;
}

export function computeValidationErrors(step, steps?) {
  return pickBy(
    {
      stepTitleTooLong: (step.stepTitle || '').length > 250,
      duplicatedTriggers: steps ? validateDuplicatedTriggers(step, steps) : false,
      titleMissing: !(step.title || '').length,
      titleTooLong: step.title.length > 250,
      endBeforeStart: step.startDelta !== undefined && step.endDelta !== undefined && step.endDelta < step.startDelta,
    },
    it => !!it
  );
}

export function validateEachStepsTriggersDuplication(modifiedSteps, steps) {
  steps.forEach(step => {
    const validationErrors = computeValidationErrors(step, steps);
    const modifiedStep = find(modifiedSteps, { id: step.id });
    if (modifiedStep) {
      modifiedStep.validationErrors = validationErrors;
    } else if (validationErrors.duplicatedTriggers) {
      step.validationErrors = validationErrors;
      modifiedSteps.push(step);
    }
  });
}
